import React from 'react';
import { motion } from 'framer-motion';
import {
  HiOutlineAcademicCap,
  HiOutlineBriefcase,
  HiOutlineGlobeAlt,
} from 'react-icons/hi2';

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

const PHOTO_URL =
  'https://forumhealth.com/wp-content/uploads/2019/05/Shilpa-Saxena-1-scaled-e1699545138555-768x768.webp';

const specialties = [
  'Gut Health',
  'Autoimmune Conditions',
  'Hormone Health',
  'Medical Weight Loss',
  'Mental Health',
  'Longevity & Regenerative Medicine',
  'Chronic Conditions',
  'Cardiometabolic Health',
];

const faculty = [
  'Institute for Functional Medicine (Senior Faculty)',
  'Andrew Weil Center for Integrative Medicine',
  'George Washington University — Metabolic Medicine Institute',
  'University of Miami Miller School of Medicine — Dept. of Family Medicine',
  'Universidad San Ignacio de Loyola, Lima, Peru',
];

function SectionLabel({ children }) {
  return (
    <div className="mb-6">
      <div className="w-8 border-t-2 border-gray-900 mb-3" />
      <h2 className="text-sm font-semibold text-gray-900">{children}</h2>
    </div>
  );
}

function YouTubeEmbed({ videoId, title }) {
  return (
    <div className="relative w-full rounded-xl overflow-hidden bg-[#0a0a0b] aspect-video">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
      />
    </div>
  );
}

export default function AboutPage() {
  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-10 pb-24">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col md:flex-row gap-10 md:gap-14 items-start mb-16"
      >
        {/* Photo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="flex-shrink-0"
        >
          <div className="w-40 h-40 md:w-48 md:h-48 rounded-2xl overflow-hidden bg-gray-100">
            <img
              src={PHOTO_URL}
              alt="Dr. Shilpa P. Saxena"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>

        {/* Bio header */}
        <div className="flex-1 min-w-0">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
            className="text-[13px] text-gray-400 uppercase tracking-wider font-medium mb-2"
          >
            Your Guide
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight leading-tight"
          >
            Shilpa P. Saxena, MD, IFMCP
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            className="text-[15px] text-gray-500 mt-1"
          >
            Board-Certified Family Physician
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-gray-400 mt-3"
          >
            <span className="flex items-center gap-1.5">
              <HiOutlineBriefcase className="w-3.5 h-3.5" />
              Chief Medical Officer, Forum Health
            </span>
            <span className="text-gray-200">|</span>
            <span className="flex items-center gap-1.5">
              <HiOutlineGlobeAlt className="w-3.5 h-3.5" />
              Medical Director, Forum Health Tampa
            </span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.4 }}
            className="text-base/7 text-gray-600 mt-6 max-w-2xl"
          >
            Dr. Shilpa Saxena is a board-certified family physician with nearly 20 years
            of progressive patient care experience, recognized internationally as an
            educator, speaker, and author. Known for her "keep it simple" solutions and
            approachable style, Dr. Saxena has made it her mission to help patients
            navigate complex health transitions with clarity, confidence, and evidence-based care.
          </motion.p>
        </div>
      </motion.div>

      {/* Introduction Video */}
      <motion.section
        custom={0}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="border-t border-gray-950/[0.08] pt-10 mb-12"
      >
        <SectionLabel>Meet Dr. Shilpa Saxena</SectionLabel>
        <div className="max-w-3xl">
          <YouTubeEmbed videoId="Ja5D2KeAcWY" title="Meet Dr. Shilpa Saxena" />
        </div>
      </motion.section>

      {/* Education */}
      <motion.section
        custom={1}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="border-t border-gray-950/[0.08] pt-10 mb-12"
      >
        <SectionLabel>Education</SectionLabel>
        <div className="space-y-4 max-w-2xl">
          <div className="flex items-start gap-4">
            <HiOutlineAcademicCap className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[15px] font-medium text-gray-900">
                University of Florida College of Medicine
              </p>
              <p className="text-[14px] text-gray-500">
                MD — Junior Honors Medical Program
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <HiOutlineAcademicCap className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[15px] font-medium text-gray-900">
                St. Vincent's Medical Center, Jacksonville, FL
              </p>
              <p className="text-[14px] text-gray-500">
                Residency — Family Practice &middot; Chief Resident &middot; Certification in Low-Risk Obstetrics
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Specialties */}
      <motion.section
        custom={2}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="border-t border-gray-950/[0.08] pt-10 mb-12"
      >
        <SectionLabel>Specialties</SectionLabel>
        <div className="flex flex-wrap gap-2 max-w-2xl">
          {specialties.map((s) => (
            <span
              key={s}
              className="px-3 py-1.5 rounded-lg bg-gray-950/[0.03] border border-gray-950/[0.06] text-[13px] text-gray-700"
            >
              {s}
            </span>
          ))}
        </div>
      </motion.section>

      {/* Faculty Positions */}
      <motion.section
        custom={3}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="border-t border-gray-950/[0.08] pt-10 mb-12"
      >
        <SectionLabel>Faculty Positions</SectionLabel>
        <ul className="space-y-3 max-w-2xl">
          {faculty.map((f) => (
            <li key={f} className="flex items-start gap-3">
              <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-gray-300" />
              <span className="text-[15px] text-gray-600">{f}</span>
            </li>
          ))}
        </ul>
      </motion.section>

      {/* Talks & Appearances */}
      <motion.section
        custom={4}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="border-t border-gray-950/[0.08] pt-10 mb-12"
      >
        <SectionLabel>Talks & Appearances</SectionLabel>
        <div className="max-w-3xl">
          <p className="text-[15px] text-gray-600 leading-relaxed mb-6">
            How to Delete Biological Aging: Guide to Biohacking
          </p>
          <YouTubeEmbed
            videoId="nh2wJusVTZk"
            title="How to Delete Biological Aging: Guide to Biohacking"
          />
        </div>
      </motion.section>

      {/* Forum Health */}
      <motion.section
        custom={5}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="border-t border-gray-950/[0.08] pt-10"
      >
        <SectionLabel>About Forum Health</SectionLabel>
        <p className="text-base/7 text-gray-600 max-w-2xl">
          Forum Health is a nationwide network of integrative and functional medicine
          providers dedicated to finding the root cause of health issues. With clinics
          across the United States, Forum Health brings personalized, evidence-based
          care to patients seeking a more thorough and empowering approach to their health.
        </p>
        <a
          href="https://forumhealth.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mt-4 transition-colors"
        >
          forumhealth.com
          <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
            <path fillRule="evenodd" d="M4.22 11.78a.75.75 0 0 1 0-1.06L9.44 5.5H5.75a.75.75 0 0 1 0-1.5h5.5a.75.75 0 0 1 .75.75v5.5a.75.75 0 0 1-1.5 0V6.56l-5.22 5.22a.75.75 0 0 1-1.06 0Z" clipRule="evenodd" />
          </svg>
        </a>
      </motion.section>
    </div>
  );
}
